import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusIcon, XIcon } from "lucide-react";
import { useRef, useState } from "react";
import { Heading } from "react-aria-components";
import z from "zod";

import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { ProfileName } from "@/components/profiles/name";
import { ProfilePhoto } from "@/components/profiles/photo";
import { levelsQueryOptions } from "@/data/levels";
import type { Division, PlayerProfile, TournamentDivision } from "@/db/schema";
import { getTournamentDivisionDisplay } from "@/hooks/tournament";
import { useTRPC } from "@/integrations/trpc/react";

export type AddTeamFormProps = {
  tournamentId: number;
  division: TournamentDivision & { division: Division };
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
};

export function AddTeamForm({
  division,
  isOpen: controlledIsOpen,
  onOpenChange: controlledOnOpenChange,
  hideTrigger,
}: AddTeamFormProps) {
  const trpc = useTRPC();

  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = controlledIsOpen ?? uncontrolledOpen;
  const setOpen = controlledOnOpenChange ?? setUncontrolledOpen;

  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    ...trpc.tournaments.addTeam.mutationOptions(),
  });

  const profiles = useRef<PlayerProfile[]>([]);

  const [selectedProfiles, setSelectedProfiles] = useState<
    (PlayerProfile | null)[]
  >([]);

  const schema = z.object({
    players: z.array(z.number()).length(division.teamSize),
    bypassWaitlist: z.boolean(),
  });

  const defaultValues = {
    players: Array.from({ length: division.teamSize }).map(
      () => null as null | number,
    ),
    bypassWaitlist: false,
  };

  const form = useAppForm({
    defaultValues,
    validators: {
      onMount: schema,
      onChange: schema,
    },
    onSubmit: ({ value: { players, bypassWaitlist }, formApi }) => {
      mutate(
        {
          tournamentDivisionId: division.id,
          players: players as number[],
          bypassWaitlist,
        },
        {
          onSuccess: () => {
            setOpen(false);

            queryClient.invalidateQueries(trpc.tournaments.pathFilter());

            formApi.reset(defaultValues);

            setSelectedProfiles([]);
          },
        },
      );
    },
  });

  const { data: levelsForDivision } = useQuery(
    levelsQueryOptions({ division: division.division.order }),
  );

  return (
    <>
      {!hideTrigger && (
        <Button variant="icon" onPress={() => setOpen(true)}>
          <PlusIcon size={12} />
        </Button>
      )}

      <Modal isOpen={isOpen} onOpenChange={setOpen}>
        <div className="relative flex flex-col space-y-4 p-3">
          <Heading className={title({ size: "sm" })} slot="title">
            Add Team
          </Heading>

          <p>
            Add a team to the{" "}
            <span className="font-bold italic">
              {getTournamentDivisionDisplay(division)}
            </span>{" "}
            division for free.
          </p>

          <form
            onSubmit={(e) => {
              e.preventDefault();

              form.handleSubmit();
            }}
            className="flex flex-col space-y-2"
          >
            <form.AppField
              name="players"
              mode="array"
              children={(field) =>
                field.state.value.map((_, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: form array fields keyed by position
                  <form.AppField key={i} name={`players[${i}]`}>
                    {(subField) => {
                      const player = selectedProfiles[i];

                      return player ? (
                        <div className="flex flex-row items-center justify-between">
                          <span className="flex flex-row space-x-2">
                            <ProfilePhoto {...player} />
                            <ProfileName {...player} />
                          </span>
                          <Button
                            className="text-red-500"
                            variant="icon"
                            onPress={() => {
                              subField.handleChange(null);

                              setSelectedProfiles((state) => {
                                const next = [...state];
                                next[i] = null;
                                return next;
                              });
                            }}
                          >
                            <XIcon size={16} />
                          </Button>
                        </div>
                      ) : (
                        <subField.AsyncComboBox
                          isRequired
                          className="col-span-3"
                          label="Player"
                          field={subField}
                          onSelectionChange={(next) => {
                            const player = profiles.current?.find(
                              ({ id }) => id === next,
                            );

                            setSelectedProfiles((state) => {
                              const next = [...state];
                              next[i] = player || null;
                              return next;
                            });
                          }}
                          fetchOptions={{
                            load: async ({ filterText }) => {
                              if (!filterText || filterText.length < 3) {
                                return { items: [] };
                              }

                              const result = await queryClient.ensureQueryData(
                                trpc.profiles.list.queryOptions({
                                  query: filterText,
                                  levels: levelsForDivision?.map(
                                    ({ id }) => id,
                                  ),
                                  gender:
                                    division.gender !== "coed"
                                      ? division.gender
                                      : undefined,
                                }),
                              );

                              profiles.current = result;

                              return {
                                items: result.map(
                                  ({
                                    id,
                                    preferredName,
                                    firstName,
                                    lastName,
                                    ...profile
                                  }) => ({
                                    beforeDisplay: (
                                      <ProfilePhoto
                                        preferredName={preferredName}
                                        firstName={firstName}
                                        lastName={lastName}
                                        {...profile}
                                      />
                                    ),
                                    display: `${preferredName || firstName} ${lastName}`,
                                    value: id,
                                  }),
                                ),
                              };
                            },
                          }}
                        />
                      );
                    }}
                  </form.AppField>
                ))
              }
            />

            <form.AppField
              name="bypassWaitlist"
              children={(field) => (
                <field.Checkbox field={field} label="Bypass Waitlist" />
              )}
            />

            <form.AppForm>
              <form.Footer className="col-span-full">
                <Button onPress={() => setOpen(false)}>Cancel</Button>

                <form.SubmitButton>Add Team</form.SubmitButton>
              </form.Footer>
            </form.AppForm>
          </form>
        </div>
      </Modal>
    </>
  );
}
